import {
  findNodeById,
  findParentById,
  isTextNode,
  moveNode,
  normalizeClassList,
} from './htmlEditorUtils.js';
import {
  getManagedNodeCapabilities,
  isManagedCollectionNode,
} from './htmlCollectionUtils.js';
import {
  canPortfolioElementAcceptChild,
} from './portfolioElementRegistry.jsx';

export const EMPTY_LAYER_MOVE_OPTIONS = Object.freeze({
  canChangeLayer: false,
  canMoveUp: false,
  nodeLabel: '',
  parentCandidates: Object.freeze([]),
});

const shortenText = (content, maximumLength = 32) => {
  const normalized = String(content || '').replace(/\s+/g, ' ').trim() || 'Empty text';
  return normalized.length > maximumLength
    ? `${normalized.slice(0, maximumLength - 1)}...`
    : normalized;
};

export const getLayerNodeLabel = (node) => {
  if (isTextNode(node)) return `text "${shortenText(node.content)}"`;

  const classes = normalizeClassList(node?.attributes?.class);
  return `${node?.type || 'element'}${classes.map((className) => `.${className}`).join('')}`;
};

const createInsertionSlots = (parentNode) => {
  const children = Array.isArray(parentNode?.children) ? parentNode.children : [];
  if (children.length === 0) {
    return [{
      id: `inside-${parentNode.id}`,
      label: 'Inside empty container',
      targetId: parentNode.id,
      position: 'child',
    }];
  }

  return [
    {
      id: `before-${children[0].id}`,
      label: 'At beginning',
      targetId: children[0].id,
      position: 'before',
    },
    ...children.map((child, index) => ({
      id: `after-${child.id}`,
      label: index === children.length - 1
        ? `At end, after ${getLayerNodeLabel(child)}`
        : `After ${getLayerNodeLabel(child)}`,
      targetId: child.id,
      position: 'after',
    })),
  ];
};

export const getLayerMoveOptions = (root, nodeId) => {
  const selectedNode = findNodeById(root, nodeId);
  const parentNode = findParentById(root, nodeId);
  if (!selectedNode || isTextNode(selectedNode) || !parentNode) {
    return EMPTY_LAYER_MOVE_OPTIONS;
  }

  const capabilities = getManagedNodeCapabilities(selectedNode);
  if (!capabilities.canChangeLayer) {
    return EMPTY_LAYER_MOVE_OPTIONS;
  }

  const grandparentNode = findParentById(root, parentNode.id);
  const canMoveUp = Boolean(
    grandparentNode
    && parentNode.id !== root.id
    && !isManagedCollectionNode(grandparentNode)
    && canPortfolioElementAcceptChild(grandparentNode.type, selectedNode.type)
  );

  const parentCandidates = (parentNode.children || [])
    .filter((candidate) => (
      !isTextNode(candidate)
      && candidate.id !== selectedNode.id
      && !isManagedCollectionNode(candidate)
      && canPortfolioElementAcceptChild(candidate.type, selectedNode.type)
    ))
    .map((candidate) => ({
      id: candidate.id,
      label: getLayerNodeLabel(candidate),
      slots: createInsertionSlots(candidate),
    }));

  return {
    canChangeLayer: true,
    canMoveUp,
    nodeLabel: getLayerNodeLabel(selectedNode),
    parentCandidates,
  };
};

export const moveNodeUpLayer = (root, nodeId) => {
  const options = getLayerMoveOptions(root, nodeId);
  if (!options.canMoveUp) return root;

  const parentNode = findParentById(root, nodeId);
  return parentNode ? moveNode(root, nodeId, parentNode.id, 'after') : root;
};

export const moveNodeDownLayer = (root, nodeId, parentId, slotId) => {
  const options = getLayerMoveOptions(root, nodeId);
  const parent = options.parentCandidates.find((candidate) => candidate.id === parentId);
  const slot = parent?.slots.find((candidateSlot) => candidateSlot.id === slotId);
  if (!parent || !slot) return root;

  return moveNode(root, nodeId, slot.targetId, slot.position);
};
